import React, { useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { DateTime } from "luxon";
import * as yup from "yup";

import { Divider, Typography } from "@mui/material";

import Button from "@readai/common/components/Button";
import useAuth from "@readai/common/hooks/useAuth";
import usePageTitle from "@readai/common/hooks/usePageTitle";
import { getTimezoneName } from "@readai/common/utils";
import { formatStartAndEndTime } from "@readai/common/utils/datetimeUtils";
import yupResolver from "@readai/common/utils/yupResolver";
import Stack from "@readai/pulse/components/Stack";

import ReadSmartSchedulerLogo from "@app/booking/components/ReadSmartSchedulerLogo";
import {
  InputWrapper,
  StyledFormLabel,
  StyledTextField,
} from "@app/booking/components/styles";
import useRssLinkCalendarEvent from "@app/booking/hooks/useRssLinkCalendarEvent";

import EventCanceled from "./notifications/EventCanceled";

const CANCEL_REASON_CHARACTER_LIMIT = 500;

const getCancelEventSchema = () =>
  yup.object().shape({
    name: yup.string().required(t`Your name is required to continue`),
    email: yup.string().email(t`Must be a valid email`),
  });

interface CancelFieldValues {
  name: string;
  email?: string;
}

const CancelEvent = () => {
  usePageTitle(t`Event - Cancel`, {
    eventTitle: "Event - Cancel",
  });

  const { _, i18n } = useLingui();
  const { user } = useAuth();

  const schema = useMemo(getCancelEventSchema, [_]);

  const form = useForm<CancelFieldValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: user ? user.fullName : "",
      email: user ? user.email : "",
    },
  });

  const [cancelReason, setCancelReason] = useState<string>("");
  const [queryParams, setQueryParams] = useSearchParams();

  const cancelSuccess = queryParams.get("success") === "true";

  const {
    getEvent: { data: event },
    cancelEvent: {
      mutate: cancelRssLinkCalendarEvent,
      isPending: cancelIsLoading,
    },
  } = useRssLinkCalendarEvent();

  const cancelEvent = ({ name, email }: CancelFieldValues) => {
    cancelRssLinkCalendarEvent(
      {
        eventId: event!.id,
        cancelEventBody: { reason: cancelReason, name, email },
      },
      {
        onSuccess: () => {
          setQueryParams({ success: "true" });
        },
      },
    );
  };

  if (cancelSuccess) {
    return <EventCanceled ownerName={event?.ownerName} />;
  }

  if (!event) return null;

  const remainingCancelReasonCharacters =
    CANCEL_REASON_CHARACTER_LIMIT - cancelReason.length;

  return (
    <Stack
      spacing={2}
      justifyContent="space-between"
      width="500px"
      maxWidth="100%"
    >
      <ReadSmartSchedulerLogo />
      <Typography variant="h2" fontWeight={500}>
        {event.title}
      </Typography>
      <Divider />
      <Stack spacing={0.5} sx={{ color: "text.weak", pb: 1.5 }}>
        <Typography variant="bodyPrimaryMed">
          {event.startTime.toLocaleString(DateTime.DATE_HUGE)}
        </Typography>
        <Typography variant="bodyPrimaryMed">
          {formatStartAndEndTime(event.startTime, event.endTime, {
            locale: i18n.locale,
          })}
        </Typography>
        <Typography variant="bodySecondaryReg">
          {event.startTime.toFormat("ZZZZZ")}{" "}
          {event.startTime.zone.name
            ? `- ${getTimezoneName(event.startTime.zone.name)}`
            : ""}
        </Typography>
      </Stack>
      <InputWrapper>
        <StyledFormLabel required>
          <Trans>Your Name</Trans>
        </StyledFormLabel>
        <StyledTextField
          {...form.register("name")}
          error={Boolean(form.formState.errors.name)}
          helperText={form.formState.errors.name?.message}
        />
      </InputWrapper>
      <InputWrapper>
        <StyledFormLabel>
          <Trans>Your Email</Trans>
        </StyledFormLabel>
        <StyledTextField
          {...form.register("email")}
          error={Boolean(form.formState.errors.email)}
          helperText={form.formState.errors.email?.message}
        />
      </InputWrapper>
      <InputWrapper>
        <StyledFormLabel>
          <Trans>Reason for cancelling</Trans>
        </StyledFormLabel>
        <StyledTextField
          multiline
          rows={5}
          onChange={(event) => setCancelReason(event.target.value)}
          className="text-field__textarea"
          value={cancelReason}
          inputProps={{ maxLength: CANCEL_REASON_CHARACTER_LIMIT }}
          helperText={
            remainingCancelReasonCharacters < CANCEL_REASON_CHARACTER_LIMIT
              ? t`Max ${CANCEL_REASON_CHARACTER_LIMIT} characters (${remainingCancelReasonCharacters} remaining)`
              : t`Max ${CANCEL_REASON_CHARACTER_LIMIT} characters`
          }
        />
      </InputWrapper>
      <Button
        type="submit"
        loading={cancelIsLoading}
        onClick={form.handleSubmit(cancelEvent)}
        color="error"
        sx={{ width: "fit-content" }}
      >
        <Trans>Cancel event</Trans>
      </Button>
    </Stack>
  );
};

export default CancelEvent;
